<template>
  <div class="animated fadeIn">
    <b-container fluid class="order-list">

      <div v-show="errorText !== ''" class="alert alert-danger" role="alert">
        {{errorText}}
      </div>

      <b-row class="filter-row">
        <b-col lg="4">
          <b-form-group
            :label="$t('Buscar')"
            :invalid-feedback="!isValidText(filter, false)? 'No es un valor válido' : ''"
            :state="isValidText(filter, false)"
            class="mb-0"
            :description="$t('Ingrese el texto a buscar')">
            <b-input-group>
              <b-form-input v-model="filter" placeholder="Texto a buscar" />
              <b-input-group-button>
                <b-btn :disabled="!filter" @click="filterTable">Buscar</b-btn>
              </b-input-group-button>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col lg-="8" class="mt-4">
          <button type="button" class="btn btn-primary float-right"  @click.stop="showFilters = !showFilters">
            <i class="fa" :class="{'fa-times': showFilters, 'fa-filter': !showFilters}" />{{$t('Filtros')}}
          </button>
        </b-col>
      </b-row>

      <transition name="fade">
        <div class="filters mt-3 p-3" v-show="showFilters">
          <b-row>
            <b-col lg="4">
              <b-form-group :label="$t('Desde')" class="mb-0" :description="$t('Fecha de inicio para filtrar datos')"  :state="invalidDateFrom === '' ? true : false" :invalid-feedback="invalidDateFrom">
                <b-input-group>
                  <date-picker v-on:contextmenu.native.stop.prevent v-on:keydown.native.stop.prevent v-model="fromDate" :config="config"></date-picker>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col  lg="4">
              <b-form-group :label="$t('Hasta')" class="mb-0" :description="$t('Fecha final para filtrar datos')"  :state="invalidDateTo === '' ? true : false" :invalid-feedback="invalidDateTo">
                <b-input-group>
                  <date-picker v-on:contextmenu.native.stop.prevent v-on:keydown.native.stop.prevent v-model="toDate" :config="config"></date-picker>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('Estado')" class="mb-3" :description="$t('Seleccione el estado de la orden')">
                <b-form-select v-model="statusName">
                  <option slot="first" :value="null">{{$t('Todos')}}</option>
                  <option v-for="(text, value) in statusMapping" 
                      :key="value"
                      :value="value">{{$t(text)}}</option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="4">
              <b-form-group 
                label="Filtrar por TAGS"
                description="Selecciones los tags de ordenes que desea filtrar en la busqueda"
              >
                <b-form-checkbox-group v-model="filterTags" stacked>
                  <template v-for="tagElem in tagsMapping">
                    <b-form-checkbox 
                      v-if="!tagElem.isCt"
                      :key="tagElem.key"
                      :value="tagElem.key"
                    >
                      <span class="tag-check p-1" :class="tagElem.bg">{{tagElem.text}}</span>
                    </b-form-checkbox>
                  </template>
                </b-form-checkbox-group>
             </b-form-group>
            </b-col>
            <b-col lg="4" v-if="isAdmin">
              <b-form-group label="Seller" class="mb-3">
                <b-form-select :options="organizations" v-model="account">
                  <option slot="first" :value="null">{{$t('Todos')}}</option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-input-group-button class="float-right mt-3">
                <b-button :disabled="loading || disableFilter" :block="true" variant="primary" @click.stop="filterTable" >Filtrar</b-button>
              </b-input-group-button>
              <b-input-group-button class="float-right mt-3 mr-3">
                <b-button :disabled="loading" :block="true" variant="primary" @click.stop="cleanFilters" >Mostrar Todo</b-button>
              </b-input-group-button>
            </b-col>
          </b-row>
        </div>
      </transition>

      <b-row class="mt-3">
        <b-col class="col-md-auto">
          <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage" class="mb-3" />
        </b-col>
        <b-col class="col-md-auto">
          <b-form-group
            id="perPage"
            horizontal
            :label="$t('Mostrar por página')"
            >
            <b-form-select :options="pageOptions" v-model="perPage" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="my-3">
        <b-col md="12">
          <b>{{$tc('{count} ordenes encontradas', totalRows, { count: totalRows })}}</b>
        </b-col>
      </b-row>
      <b-card header-bg-variant="primary" border-variant="primary" :header="$t('Listado de ordenes')">
        <moon-loader :loading="loading" color="#000" size="30px"></moon-loader>
        <b-table v-show="!loading" id="mainTable"
           ref="mainTable"
           show-empty
           striped
           hover
           stacked="xl"
           :items="itemProvider"
           foot-clone
           :fields="isAdmin ? fields: fieldsNotAdmin"
           :current-page="currentPage"
           :per-page="perPage"
           :sort-by.sync="sortBy"
           :sort-desc.sync="sortDesc"
           @row-clicked="rowClick"
           @filtered="onFiltered"
           :empty-text="$t('No hay ordenes para mostrar')"
           :empty-filtered-text="$t('No hay ordenes para mostrar')">
          <template slot="clientProfileData" slot-scope="data">
            {{data.value ? data.value.firstName : ''}} {{data.value ? data.value.lastName : ''}}
          </template>
          <template slot="status" slot-scope="status">
            <span :class="getStatusColorClass(status.value)"><b>{{$t(statusMapping[status.value])}}</b></span>
          </template>
          <template slot="creationDate" slot-scope="creationDate">
            {{new Date(creationDate.value).toLocaleDateString(locale)}}
          </template>
          <template slot="value" slot-scope="value">
            {{currency}} {{value.value}}
          </template>
          <template slot="tags" slot-scope="row">
              <span
                v-for="tagElemKey in row.item.tags"
                v-b-tooltip.hover
                :title="tagsMapping[tagElemKey].description"
                class="tag"
                :class="tagsMapping[tagElemKey].bg"
              >
                {{tagElemKey}}
              </span>
          </template>
        </b-table>
      </b-card>
      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage" class="my-0" />
        </b-col>
      </b-row>

    </b-container>
  </div>
</template>

<script>
// Date Picker
import Vue from 'vue'
import datePicker from 'vue-bootstrap-datetimepicker'
import 'eonasdan-bootstrap-datetimepicker/build/css/bootstrap-datetimepicker.css'
import {OrdersServiceMixim} from '@/mixims/OrdersServiceMixim.js'
import appConfig from '@/config/applicationConfig.js'
import MoonLoader from 'vue-spinner/src/MoonLoader.vue'
import { ValidationsMixim } from '@/mixims/ValidationsMixim.js'

Vue.use(datePicker)

export default {
  name: 'order-list',
  mixins: [OrdersServiceMixim, ValidationsMixim],
  components: {MoonLoader},
  data () {
    return {
      fields: [
        { key: 'id', label: this.$t('Número'), 'class': 'text-left', sortable: true },
        { key: 'accountName', label: this.$t('Seller'), 'class': 'text-left', sortable: true },
        { key: 'clientProfileData', label: this.$t('Cliente'), 'class': 'text-left', sortable: true },
        { key: 'creationDate', label: this.$t('Fecha'), 'class': 'text-left', sortable: true },
        { key: 'value', label: this.$t('Total'), 'class': 'text-left', sortable: true },
        { key: 'status', label: this.$t('Estado'), 'class': 'text-left', sortable: true },
        { key: 'tags', label: this.$t('Etiquetas'), 'class': 'text-left' }
      ],
      fieldsNotAdmin: [
        { key: 'id', label: this.$t('Número'), 'class': 'text-left', sortable: true },
        { key: 'clientProfileData', label: 'Cliente', 'class': 'text-left', sortable: true },
        { key: 'creationDate', label: 'Fecha', 'class': 'text-left', sortable: true },
        { key: 'value', label: 'Total', 'class': 'text-left', sortable: true },
        { key: 'status', label: 'Estado', 'class': 'text-left', sortable: true },
        { key: 'tags', label: 'Etiquetas', 'class': 'text-left', sortable: false }
      ],
      isAdmin: this.$store.getters.isAdmin,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      pageOptions: [ 10, 15, 25, 50, 100 ],
      account: null,
      sortBy: null,
      sortDesc: false,
      filter: null,
      filterTags: [],
      errorText: '',
      statusName: null,
      fromDate: new Date(),
      toDate: new Date(),
      showFilters: false,
      loading: false,
      locale: appConfig.LOCALE,
      currency: appConfig.CURRENCY,
      config: {
        format: 'YYYY-MM-DD',
        useCurrent: false,
        icons: {
          previous: 'fa fa-chevron-left',
          next: 'fa fa-chevron-right'
        }
      },
      statusMapping: appConfig.STATUS_MAPPING,
      statusMappingColors: appConfig.STATUS_MAPPING_COLORS,
      tagsMapping: appConfig.TAGS_MAPPING
    }
  },
  computed: {
    disableFilter () {
      return this.invalidDateTo !== '' || this.invalidDateFrom !== '' || !this.isValidText(this.filter)
    },
    sortOptions () {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => { return { text: f.label, value: f.key } })
    },
    organizations () {
      return this.$store.getters.organizations
    },
    invalidDateTo () {
      if (this.toDate) {
        let tDate = new Date(this.toDate)
        if (tDate > new Date()) {
          return 'La fecha seleccionada es mayor al día de hoy'
        }
        if (this.fromDate) {
          let fDate = new Date(this.fromDate)
          return fDate > tDate ? 'La fecha final es menor a la fecha inicial' : ''
        }
      }
      return ''
    },
    invalidDateFrom () {
      if (this.fromDate) {
        let fDate = new Date(this.fromDate)
        if (fDate > new Date()) {
          return 'La fecha seleccionada es mayor al día de hoy'
        }
      }
      return ''
    }
  },
  methods: {
    getStatusColorClass: function (status) {
      return 'text-' + this.statusMappingColors[status] + ' h6'
    },
    controversyTagClass: function (row) {
      let days = 0
      if (row.controversies && row.controversies.length > 0) {
        let auxDays = 0
        row.controversies.forEach((cont) => {
          let timeDiff = (new Date()) - (new Date(cont.updateDate))
          auxDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
          days = auxDays >= days ? auxDays : days
        })
      }
      if (days < 1) return 'green'
      if (days < 2) return 'yellow'
      return 'red'
    },
    showControversyTag (orderDesc) {
      if (orderDesc.controversies !== null && orderDesc.controversies.length > 0) {
        return (orderDesc.controversies.find(
          (item) => {
            return item.state !== 'INVALID' && item.state !== 'DONE'
          }) !== undefined)
      }
      return false
    },
    rowClick (item) {
      this.$router.push(`/orders/orderDetail/${item.id}`)
    },
    onFiltered (filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    filterTable () {
      this.$refs.mainTable.refresh()
    },
    cleanFilters () {
      this.filter = null
      this.filterTags = []
      this.statusName = null
      this.fromDate = ''
      this.toDate = ''
      this.filterTable()
    },
    itemProvider (tableComponentInfo) {
      this.loading = true
      this.errorText = ''
      return this.getAllOrders(tableComponentInfo).then(function (response) {
        let items = response.body
        this.totalRows = parseInt(response.headers.get('X-Total-Count'))
        this.loading = false
        return items
      },
      (error) => {
        this.loading = false
        this.errorText = error.body.message || 'Error obteniendo el listado de pedidos'
        return []
      })
    }
  },
  mounted () {
    this.fromDate = ''
    this.toDate = ''

    setInterval(() => {
      this.$refs.mainTable.refresh()
    }, appConfig.ORDER_LIST_TTL)
  }
}
</script>
<style lang="scss" scoped>
  .order-list {
    .orderTable {
      table-layout: fixed;
    }

    .filter-row {
      .btn-primary {
        width: 80px;
      }

      button {
        i {
          margin-right: 5px;
        }
      }
    }

    .filters {
      border: 1px solid #e3e8ec;
      background-color: #FFF
    }

    .fade-enter-active, .fade-leave-active {
      transition: opacity .5s;
    }

    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
      opacity: 0;
    }

    .tag {
      width: 26px;
      display: inline-block;
      border-radius: 5px;
      line-height: 17px;
      margin-right: 4px;
      cursor: pointer;
      text-align: center;

      &.green {
        background: #4DBD74;
        color: #FFFFFF;
      }
      &.yellow {
        background: #e8f48b;
      }
      &.red {
        background: #f23c3c;
        color: #FFFFFF;
      }
    }

    tr:hover {
      cursor: pointer
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/common/OrderList.vue